import api from '@/shared/api';
import { formatDateForApi, requestExcelBlobExport } from '@/shared/lib';
import type { OperationLogResponse } from '../model/OperationLogResponse';
import type {
  OperationLogDocumentTypeFilter,
  OperationLogEventFilter,
} from '../model/operation-log.types';

export interface OperationLogListParams {
  page?: number;
  limit?: number;
  sort?: 'occurredAt';
  order?: 'asc' | 'desc';
  search?: string;
  searchField?: 'ip_address' | 'description';
  startDate?: Date | null;
  endDate?: Date | null;
  userId?: number | null;
  positionName?: string | null;
  documentType?: OperationLogDocumentTypeFilter | null;
  event?: OperationLogEventFilter | null;
}

export interface OperationLogApiResponse {
  success: boolean;
  data: OperationLogResponse[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    itemsPerPage?: number;
    totalPages: number;
  };
}

function buildOperationLogQuery(params: OperationLogListParams): URLSearchParams {
  const queryParams = new URLSearchParams();

  const entries: Array<[string, string | null | undefined]> = [
    ['page', params.page ? String(params.page) : undefined],
    ['limit', params.limit ? String(params.limit) : undefined],
    ['sort', params.sort],
    ['order', params.order],
    ['search', params.search],
    ['searchField', params.search ? params.searchField : undefined],
    ['startDate', params.startDate ? formatDateForApi(params.startDate) : undefined],
    ['endDate', params.endDate ? formatDateForApi(params.endDate) : undefined],
    ['userId', params.userId ? String(params.userId) : undefined],
    ['positionName', params.positionName ? params.positionName : undefined],
    ['documentType', params.documentType],
    ['event', params.event],
  ];

  for (const [key, value] of entries) {
    if (value) queryParams.append(key, value);
  }

  return queryParams;
}

class OperationLogApi {
  async getOperationLogs(params: OperationLogListParams = {}): Promise<OperationLogApiResponse> {
    const suffix = buildOperationLogQuery(params).toString();
    const url = suffix ? `/audit-logs?${suffix}` : '/audit-logs';
    const response = await api.get(url);

    return response.data;
  }

  async exportOperationLogsExcel(
    params: OperationLogListParams = {}
  ): Promise<{ blob: Blob; exportedCount: number; filename: string }> {
    const query = buildOperationLogQuery({ ...params, page: undefined, limit: undefined });

    return requestExcelBlobExport(
      () => api.get(`/audit-logs/export/excel?${query.toString()}`, { responseType: 'blob' }),
      'logs-operacoes.xlsx',
      'Erro ao exportar. Tente novamente.'
    );
  }
}

export const operationLogApi = new OperationLogApi();
